import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useUpsertAppParam } from "src/api/client-api/appParams";
import { FoodsSelect } from "@foodpilot/foods";
import {
    ApplicationParameterKeys,
    appParamCacheKey,
    useApplicationParameters,
} from "src/api/client-api/appParams/useApplicationParameters";
import { useQueryClient } from "react-query";
import { useDeleteAppParam } from "src/api/client-api/appParams/useDeleteAppParam";

type Choice = {
    id: string | number;
    label: string;
    valueToStore: string;
};

type BaseSelectUploadProps = {
    title?: string;
    appParamKey: ApplicationParameterKeys;
    allChoices: Choice[];
    disabled?: boolean;
};
export const BaseSelectUpload = (props: BaseSelectUploadProps) => {
    const { title, appParamKey, allChoices, disabled = false } = props;
    const { applicationParameters, isLoading } = useApplicationParameters();
    const upsertAppParam = useUpsertAppParam({ forceReload: false });
    const deleteAppParam = useDeleteAppParam({ forceReload: false });
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    if (isLoading) return;
    if (applicationParameters === undefined) return;

    const originalValue = applicationParameters[appParamKey] ?? null;
    const foundExistingValue = allChoices.find((v) => v.id === originalValue) ?? null;

    return (
        <Box sx={{ display: "flex", flexDirection: "row", height: "75px", gap: "16px" }}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography variant="big-bold">{title}</Typography>
                <FoodsSelect
                    selectedOption={foundExistingValue}
                    options={allChoices}
                    noValue={t("component.select.unselectvalue")}
                    onChange={(selectedItem) => {
                        if (selectedItem === null) {
                            deleteAppParam.mutate(
                                { key: appParamKey },
                                {
                                    onSuccess: () => {
                                        queryClient.refetchQueries([appParamCacheKey]);
                                    },
                                },
                            );
                            return;
                        }

                        const payload = {
                            isNew: originalValue === null ? true : false,
                            key: appParamKey,
                            value: selectedItem.valueToStore,
                        };
                        upsertAppParam.mutate(payload, {
                            onSuccess: () => {
                                queryClient.refetchQueries([appParamCacheKey]);
                            },
                        });
                    }}
                    getId={(item) => item.id}
                    getName={(item) => item.label}
                    textOptions={{
                        placeholder: t("component.select.placeholder"),
                        searchPlaceholder: t("component.select.searchplaceholder"),
                        emptyOptionsText: t("component.select.nooptions"),
                        defaultValueText: t("component.select.defaultvalue"),
                        noResult: t("component.select.noresultfound"),
                    }}
                    disabled={disabled}
                />
            </Box>
        </Box>
    );
};
